import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Ban, Flag, MessageSquare, Clock3 } from "lucide-react";
import { toast } from "sonner";

import { useI18n } from "@/i18n/I18nContext";
import { api } from "@/lib/api";
import { shortName, interestText, sharedInterests, findContact, sentAt, contactHref } from "@/lib/v3People";
import { useMemberAuth } from "../MemberAuthContext";
import { useCore } from "../useCore";
import { requestContact, runAction } from "../actions";
import { ConnectionError } from "../components/ConnectionError";
import { ReportDialog } from "../components/ReportDialog";
import { Loading, Unavailable, ErrorNote } from "../components/kit";
import { V3Title, V3Columns, V3Actions, DetailRow, DetailList, AsideCard, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { AreaCard, ResponsibleCard, ResultTable, ResultRow, V3_GHOST, V3_LABEL } from "../components/v3app";
import { InfoBox, StagePill } from "../components/v3people";
import { usePerson, PersonMissing } from "./PersonDetail";

const useConvs = () => useCore("/member/conversations", (d) => (Array.isArray(d.conversations) ? d.conversations : []));
const place = (m) => [shortName(m.name), m.city].filter(Boolean).join(" · ");

function Keyed({ children }) {
  const { member } = useMemberAuth();
  const params = useParams();
  return <div key={`${member?.id || "anon"}:${params.id}`}>{children(params.id)}</div>;
}

function Compose({ m }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const p = useCore("/member/profile", (d) => d.profile || {});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const first = shortName(m.name).split(" ")[0];
  const common = p.data ? interestText(sharedInterests(p.data.interests, m.interests), (k) => t(`member.categories.${k}`)) : "";
  const send = async () => {
    setBusy(true); setError("");
    const err = await requestContact(m.id);
    if (err) { setBusy(false); setError(err); return; }
    toast.success("Kontaktanfrage gesendet.");
    const conv = await api.get("/member/conversations")
      .then((r) => (Array.isArray(r.data.conversations) ? r.data.conversations : []).find((c) => c.type === "direct" && c.other?.id === m.id && c.status === "pending" && c.is_requester) || null)
      .catch(() => null);
    navigate(conv ? contactHref("outgoing", conv.id) : "/kontakte", { replace: true });
  };
  return (
    <div data-m="M53" data-testid="contact-compose">
      <V3Title title="Kontaktanfrage formulieren" intro={`Sende ${first} eine freundliche Anfrage. Direkte Nachrichten folgen erst nach Annahme.`} testid="contact-compose-title" />
      <V3Columns layout="app" aside={<>
        <AreaCard title="Nachrichtenvorschau" icon={MessageSquare} text="Ohne persönliche Nachricht – das Mitsenden eines Textes ist noch nicht verfügbar." testid="contact-compose-preview" />
        <AsideCard icon={Clock3} tone="white" title="Versandstatus" text="Entwurf · Noch nicht gesendet" testid="contact-compose-state" />
      </>} main={<>
        <p className={V3_LABEL}>Empfänger</p>
        <p className="mb-[18px] flex min-h-[52px] items-center rounded-[8px] border border-v3-line bg-white px-[14px] text-[14px] text-v3-ink lg:text-[15px]" data-testid="contact-compose-recipient">{place(m)}</p>
        <label htmlFor="contact-compose-text" className={V3_LABEL}>Dein Text</label>
        <textarea id="contact-compose-text" disabled rows={3} placeholder="Noch nicht verfügbar – die Anfrage wird ohne Text gesendet." aria-describedby="contact-compose-text-hint" data-testid="contact-compose-text"
          className="min-h-[112px] w-full cursor-not-allowed resize-none rounded-[8px] border border-v3-line bg-white px-[14px] py-[14px] text-[14px] text-v3-ink placeholder:text-v3-muted lg:text-[15px]" />
        <span id="contact-compose-text-hint" className="mb-[18px] mt-[6px] block text-[13px] text-v3-muted">Der gemeinsame Core übermittelt noch keine Nachricht zur Kontaktanfrage.</span>
        <DetailList>
          <DetailRow label="Gemeinsame Interessen" value={!p.data ? "Wird geladen …" : common || "Keine gemeinsamen Interessen angegeben"} testid="contact-compose-common" />
          <DetailRow label="Status" value="Kontaktanfrage noch nicht gesendet" testid="contact-compose-status" />
        </DetailList>
        {error && <div className="mt-[12px]"><ErrorNote testid="contact-compose-error">{error}</ErrorNote></div>}
        <V3Actions>
          <button type="button" onClick={send} disabled={busy} data-testid="contact-compose-send" className={V3_PRIMARY}>{busy ? "Wird gesendet …" : "Kontaktanfrage senden"}</button>
          <Link to={`/menschen/${encodeURIComponent(m.id)}`} data-testid="contact-compose-cancel" className={V3_SECONDARY}>Abbrechen</Link>
        </V3Actions>
      </>} />
    </div>
  );
}

// M53: request to a listed member; only a request without text can be sent today (C.3 `message`).
export function ContactCompose() {
  return <Keyed>{(id) => <ComposeLoader id={id} />}</Keyed>;
}

function ComposeLoader({ id }) {
  const { dir, person } = usePerson(id);
  const convs = useConvs();
  if (dir.error || convs.error) return <ConnectionError onRetry={() => { dir.reload(); convs.reload(); }} />;
  if (person === undefined || !convs.data) return <Loading />;
  if (!person) return <PersonMissing />;
  const existing = convs.data.find((c) => c.type === "direct" && c.other?.id === person.id && ["pending", "accepted"].includes(c.status));
  if (existing) return (<><V3Title title="Kontaktanfrage formulieren" testid="contact-compose-title" />
    <Unavailable testid="contact-compose-exists">Zu dieser Person gibt es bereits eine offene Anfrage oder einen bestätigten Kontakt. Eine zweite Anfrage wird nicht gesendet.</Unavailable>
    <Link to={`/menschen/${encodeURIComponent(person.id)}`} className={`${V3_SECONDARY} mt-[18px]`}>Zum Profil</Link></>);
  return <Compose m={person} />;
}

function NotFoundRequest({ testid }) {
  return (<><V3Title title="Anfrage nicht gefunden" testid={`${testid}-title`} />
    <Unavailable testid={`${testid}-missing`}>Diese Anfrage gibt es nicht (mehr), sie wurde bereits beantwortet oder gehört nicht zu deinem Konto.</Unavailable>
    <Link to="/kontakte" className={`${V3_SECONDARY} mt-[18px]`} data-testid={`${testid}-back`}>Zu den Kontakten</Link></>);
}

function Incoming({ conv, convs }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const dir = useCore("/member/directory", (d) => d.members || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(false);
  const o = conv.other || {};
  const listed = (dir.data || []).find((x) => x.id === o.id) || {};
  const who = place({ name: o.name, city: listed.city });
  const answer = async (kind) => {
    setBusy(true); setError("");
    const err = await runAction("post", `/member/conversations/${encodeURIComponent(conv.id)}/${kind}`, kind === "accept" ? "Die Annahme wurde nicht gespeichert" : "Die Ablehnung wurde nicht gespeichert");
    setBusy(false);
    if (err) { setError(err); convs.reload(); return; }
    toast.success(kind === "accept" ? "Kontakt bestätigt." : "Anfrage abgelehnt.");
    navigate("/kontakte", { replace: true });
  };
  return (
    <div data-m="M54" data-testid="contact-incoming">
      <V3Title title="Eingehende Kontaktanfrage" intro={`${shortName(o.name).split(" ")[0] || "Ein Mitglied"} möchte dich kennenlernen. Du entscheidest über die Anfrage.`} testid="contact-incoming-title" />
      <V3Columns layout="app" aside={<><AreaCard title="Ausgewählter Vorgang" text={who} testid="contact-incoming-area" /><ResponsibleCard /></>} main={<>
        <StagePill testid="contact-incoming-stage">Vor der Entscheidung</StagePill>
        <DetailList>
          <DetailRow label="Absender:in" value={who || "Nicht angegeben"} testid="contact-incoming-from" />
          <DetailRow label="Interessen" value={interestText(listed.interests, (k) => t(`member.categories.${k}`)) || "Nicht angegeben"} testid="contact-incoming-interests" />
          <DetailRow label="Nachricht" value="Keine Nachricht – Nachrichten zur Anfrage sind noch nicht verfügbar" testid="contact-incoming-message" />
          <DetailRow label="Kontaktstatus" value="Anfrage offen" testid="contact-incoming-state" />
        </DetailList>
        <div className="mt-[11px]"><InfoBox title="Wirkung nach Bestätigung" text="Der Vorgang bleibt bis zu deiner ausdrücklichen Bestätigung offen. Erst danach sind direkte Nachrichten möglich; dein Access Pass bleibt privat." testid="contact-incoming-effect" /></div>
        {error && <div className="mt-[12px]"><ErrorNote testid="contact-incoming-error">{error}</ErrorNote></div>}
        <V3Actions>
          <button type="button" onClick={() => answer("accept")} disabled={busy} data-testid="contact-accept" className={V3_PRIMARY}>Kontakt annehmen</button>
          <button type="button" onClick={() => answer("decline")} disabled={busy} data-testid="contact-decline" className={V3_SECONDARY}>Ablehnen</button>
        </V3Actions>
        <div className="mt-[16px] flex flex-wrap gap-2">
          {o.id && <Link to={`/kontakte/blockieren/${encodeURIComponent(o.id)}`} data-testid="contact-incoming-block" className={V3_GHOST}><Ban className="h-4 w-4" aria-hidden="true" />Blockieren</Link>}
          <button type="button" onClick={() => setReport(true)} data-testid="contact-incoming-report" className={V3_GHOST}><Flag className="h-4 w-4" aria-hidden="true" />Melden</button>
        </div>
        <ReportDialog open={report} onOpenChange={setReport} reportedMemberId={o.id} reportedName={shortName(o.name)} conversationId={conv.id} />
      </>} />
    </div>
  );
}

// M54: incoming request; accept/decline only through the Core, success only after its answer.
export function ContactIncoming() {
  return <Keyed>{(id) => <ConvLoader id={id} kind="incoming" testid="contact-incoming" render={(conv, convs) => <Incoming conv={conv} convs={convs} />} />}</Keyed>;
}

function ConvLoader({ id, kind, testid, render }) {
  const convs = useConvs();
  if (convs.error) return <ConnectionError onRetry={convs.reload} />;
  if (!convs.data) return <Loading />;
  const conv = findContact(convs.data, id, kind);
  return conv ? render(conv, convs) : <NotFoundRequest testid={testid} />;
}

function Sent({ conv }) {
  const o = conv.other || {};
  const name = shortName(o.name);
  return (
    <div data-m="M55" data-testid="contact-sent">
      <V3Title title="Gesendete Kontaktanfrage" intro={`Deine Anfrage an ${name.split(" ")[0] || "dieses Mitglied"} ist noch nicht bestätigt.`} testid="contact-sent-title" />
      <ResultTable testid="contact-sent-table" label="Anfrage">
        <ResultRow initial="E" title="Empfänger:in" context={name || "Nicht angegeben"} to={o.id ? `/menschen/${encodeURIComponent(o.id)}` : undefined} testid="contact-sent-recipient" />
        <ResultRow initial="G" title="Gesendet" context={sentAt(conv)} action="" testid="contact-sent-at" />
        <ResultRow initial="S" title="Status" context="Ausstehend" action="" testid="contact-sent-state" />
        <ResultRow initial="D" title="Direkte Nachrichten" context="Erst nach Annahme möglich" action="" testid="contact-sent-messages" />
      </ResultTable>
      <div className="mt-[14px]"><Unavailable id="contact-withdraw-unavailable" testid="contact-withdraw-unavailable">Zurückziehen kann der gemeinsame Core noch nicht. Die Anfrage bleibt offen, bis {name || "die Person"} sie annimmt oder ablehnt.</Unavailable></div>
      <V3Actions className="mt-[21px]">
        <button type="button" disabled aria-describedby="contact-withdraw-unavailable" data-testid="contact-withdraw" className={V3_PRIMARY}>Anfrage zurückziehen</button>
        <Link to="/kontakte" data-testid="contact-sent-back" className={V3_SECONDARY}>Zurück</Link>
      </V3Actions>
    </div>
  );
}

// M55: sent request; withdrawing stays locked until POST /member/conversations/{id}/withdraw exists (C.3).
export function ContactSent() {
  return <Keyed>{(id) => <ConvLoader id={id} kind="outgoing" testid="contact-sent" render={(conv) => <Sent conv={conv} />} />}</Keyed>;
}

function Block({ who }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const name = shortName(who.name);
  const block = async () => {
    setBusy(true); setError("");
    const err = await runAction("post", `/member/blocks/${encodeURIComponent(who.id)}`, "Die Blockierung wurde nicht gespeichert");
    setBusy(false);
    if (err) { setError(err); return; }
    toast.success(`${name} ist blockiert.`);
    navigate("/sicherheit/blockiert", { replace: true });
  };
  return (
    <div data-m="M57" data-testid="contact-block">
      <V3Title title="Person blockieren bestätigen" intro={`Du kannst ${name.split(" ")[0]} blockieren und eine Meldung bei Bedarf getrennt verfassen.`} testid="contact-block-title" />
      <V3Columns layout="app" aside={<><AreaCard title="Ausgewählter Vorgang" text={name} testid="contact-block-area" /><ResponsibleCard /></>} main={<>
        <InfoBox title="Deine Bestätigung ist erforderlich" text="Prüfe den angezeigten Vorgang und die konkreten Auswirkungen." testid="contact-block-confirm-note" />
        <DetailList>
          <DetailRow label="Person" value={name} testid="contact-block-person" />
          <DetailRow label="Kontaktanfragen" value="Weitere Anfragen werden verhindert." testid="contact-block-requests" />
          <DetailRow label="Nachrichten" value="Weitere direkte Nachrichten werden verhindert." testid="contact-block-messages" />
          <DetailRow label="Meldung" value="Wird nicht automatisch erstellt" testid="contact-block-report" />
        </DetailList>
        {error && <div className="mt-[12px]"><ErrorNote testid="contact-block-error">{error}</ErrorNote></div>}
        <V3Actions>
          <button type="button" onClick={block} disabled={busy} data-testid="contact-block-confirm" className={V3_PRIMARY}>{busy ? "Wird blockiert …" : "Blockieren"}</button>
          <button type="button" onClick={() => navigate(-1)} disabled={busy} data-testid="contact-block-cancel" className={V3_SECONDARY}>Abbrechen</button>
        </V3Actions>
      </>} />
    </div>
  );
}

function BlockLoader({ id }) {
  const { dir, person } = usePerson(id);
  const convs = useConvs();
  if (dir.error || convs.error) return <ConnectionError onRetry={() => { dir.reload(); convs.reload(); }} />;
  if (person === undefined || !convs.data) return <Loading />;
  const fromConv = convs.data.find((c) => c.type === "direct" && c.other?.id === id)?.other;
  const who = person || (fromConv && fromConv.name ? fromConv : null);
  return who ? <Block who={who} /> : <PersonMissing />;
}

// M57: POST /member/blocks/{id} (Core 1.1); a report is a separate, voluntary step.
export function ContactBlock() {
  return <Keyed>{(id) => <BlockLoader id={id} />}</Keyed>;
}
